import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

import ChatService from '../services/ChatService'
import UserService from '../services/UserService'

const PAGE_SIZE = 20

const MessagesPage = ({ currentUser }) => {
  const history = useHistory()
  const [threads, setThreads] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [showArchived, setShowArchived] = useState(false)
  const [loading, setLoading] = useState(false)

  // New message compose
  const [isCreating, setIsCreating] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const [allUsers, setAllUsers] = useState([])
  const [selectedRecipient, setSelectedRecipient] = useState('')
  const [creating, setCreating] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const fetchThreads = useCallback(async () => {
    setLoading(true)
    try {
      const data = await ChatService.getThreads(page, PAGE_SIZE, showArchived)
      const items = Array.isArray(data) ? data : []
      setThreads(items)
      setTotal(items.length)
    } catch (err) {
      console.error('Failed to load threads', err)
    } finally {
      setLoading(false)
    }
  }, [page, showArchived])

  useEffect(() => {
    fetchThreads()
  }, [fetchThreads])

  useEffect(() => {
    document.title = 'GuideSmart - Messages'
    return () => {
      document.title = 'GuideSmart'
    }
  }, [])

  useEffect(() => {
    if (isCreating && allUsers.length === 0) {
      UserService.getAll().then(users => {
        setAllUsers(users.filter(u => u.id !== currentUser?.id))
      })
    }
  }, [isCreating, allUsers.length, currentUser])

  const handleTabChange = archived => {
    setShowArchived(archived)
    setPage(1)
  }

  const handleThreadClick = thread => {
    history.push(`/messages/${thread.id}`, { thread })
  }

  const handleArchive = async (e, threadId) => {
    e.stopPropagation()
    setThreads(prev => prev.filter(t => t.id !== threadId))
    setTotal(prev => Math.max(0, prev - 1))
    try {
      await ChatService.archiveThread(threadId)
    } catch {
      fetchThreads()
    }
  }

  const handleCreate = async e => {
    e.preventDefault()
    if (!newSubject.trim() || !selectedRecipient) return
    setCreating(true)
    try {
      const res = await ChatService.createThread(null, newSubject, [selectedRecipient])
      const recipientUser = allUsers.find(u => u.id === selectedRecipient)
      if (recipientUser && res) {
        if (!res.participants) res.participants = []
        res.participants.push({ userId: currentUser.id, fullName: `${currentUser.firstName} ${currentUser.lastName}` })
        res.participants.push({
          userId: recipientUser.id,
          fullName: `${recipientUser.firstName} ${recipientUser.lastName}`,
        })
      }
      setIsCreating(false)
      setNewSubject('')
      setSelectedRecipient('')
      if (res?.id) history.push(`/messages/${res.id}`, { thread: res })
    } catch (err) {
      console.error('Failed to create thread', err)
      alert('Error creating thread')
    } finally {
      setCreating(false)
    }
  }

  const getRecipientName = thread => {
    if (!thread.participants || thread.participants.length === 0) return 'Unknown'
    const other = thread.participants.find(p => p.userId !== currentUser?.id)
    return other ? other.fullName || `${other.firstName} ${other.lastName}`.trim() : 'Me'
  }

  const getRecipientInitials = thread => {
    const name = getRecipientName(thread)
    if (!name || name === 'Unknown' || name === 'Me') return '?'
    const parts = name.trim().split(' ')
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase()
  }

  const formatDate = dateStr => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.floor((now - d) / 86400000)
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' })
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Messages</h4>
        <Button variant="outline-primary" size="sm" onClick={() => setIsCreating(c => !c)}>
          {isCreating ? '✕ Cancel' : '✎ New Message'}
        </Button>
      </div>

      {/* Compose form */}
      {isCreating && (
        <div className="p-3 bg-light border rounded mb-3">
          <form onSubmit={handleCreate}>
            <div className="form-group mb-2">
              <select
                className="form-control"
                value={selectedRecipient}
                onChange={e => setSelectedRecipient(e.target.value)}
                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              >
                <option value="">Select recipient...</option>
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Subject..."
                value={newSubject}
                onChange={e => setNewSubject(e.target.value)}
              />
              <div className="input-group-append">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={creating || !selectedRecipient || !newSubject.trim()}
                >
                  Start
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <ul className="nav nav-tabs mb-0 border-0">
          <li className="nav-item">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <span
              className={`nav-link ${!showArchived ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleTabChange(false)}
            >
              Inbox
            </span>
          </li>
          <li className="nav-item">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <span
              className={`nav-link ${showArchived ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleTabChange(true)}
            >
              Archived
            </span>
          </li>
        </ul>
      </div>

      {/* Thread List */}
      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="far fa-comments fa-2x mb-2" />
          <p className="mt-2">{showArchived ? 'No archived conversations.' : 'No conversations yet.'}</p>
        </div>
      ) : (
        <div className="border rounded">
          {threads.map((thread, idx) => (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div
              key={thread.id}
              className={`d-flex align-items-center p-3 ${idx < threads.length - 1 ? 'border-bottom' : ''} ${
                thread.isUnread ? 'bg-light' : ''
              }`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleThreadClick(thread)}
            >
              {/* Unread dot */}
              <div style={{ width: 8, flexShrink: 0, marginRight: 8 }}>
                {thread.isUnread && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0d6efd' }} />}
              </div>

              {/* Avatar */}
              <div
                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white mr-3"
                style={{ width: 40, height: 40, fontSize: '1rem', flexShrink: 0 }}
              >
                {getRecipientInitials(thread)}
              </div>

              {/* Content */}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-baseline justify-content-between">
                  <span className={`text-truncate mr-2 ${thread.isUnread ? 'font-weight-bold' : ''}`}>
                    {getRecipientName(thread)}
                  </span>
                  <small className="text-muted text-nowrap">{formatDate(thread.updatedOn)}</small>
                </div>
                <div className="d-flex align-items-center">
                  <div className="text-truncate small text-muted mr-1" title={thread.subject}>
                    {thread.subject || 'No Subject'}
                    {thread.caseNumber && (
                      <span className="badge badge-info ml-1" style={{ fontSize: '0.65rem' }}>
                        Case #{thread.caseNumber}
                      </span>
                    )}
                  </div>
                </div>
                {thread.lastMessagePreview && (
                  <div className="text-truncate" style={{ fontSize: '0.82rem', color: '#888' }}>
                    {thread.lastMessagePreview}
                  </div>
                )}
              </div>

              {/* Archive / restore button */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <span
                title={showArchived ? 'Restore conversation' : 'Archive conversation'}
                className="ml-3 text-muted"
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={e => handleArchive(e, thread.id)}
              >
                <i className={`fas fa-${showArchived ? 'inbox' : 'archive'}`} />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-center mt-3 mb-5">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </Button>
          <span className="mx-3 text-muted small">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  )
}

export default MessagesPage
